import classNames from 'classnames';
import { Popup as LeafletPopup } from 'leaflet';
import { useRef } from 'react';
import { Popup } from 'react-leaflet';
import { ReactComponent as DiscountIcon } from 'src/assets/map/discount.svg';
import { ReactComponent as PhoneIcon } from 'src/assets/map/phone.svg';
import { ReactComponent as RequirementsIcon } from 'src/assets/map/requirements.svg';
import { ReactComponent as RouteIcon } from 'src/assets/map/route.svg';
import { ReactComponent as UrlIcon } from 'src/assets/map/url.svg';
import Complaint from 'src/pages/complaint/complaint';
import complaintCss from './complaint.module.scss';
import css from './marker-popup.module.scss';
import { MapCategory, type BusinessMapLocationItem } from './types';
import { ComplaintEntityType } from 'src/constants/complaint-entity-type';
import { toast } from 'react-toastify';
import { ReactComponent as CopyIcon } from 'src/assets/ContentCopy.svg';

interface MarkerPopupProps {
  data: BusinessMapLocationItem;
  category: MapCategory;
}

export default function MarkerPopup({ data, category }: Readonly<MarkerPopupProps>) {
  const popupRef = useRef<LeafletPopup>(null);

  function copyToClipboard(text: string | undefined) {
    if (text) {
      navigator.clipboard.writeText(text);
      toast.success('Скопійовано в буфер обміну');
    }
  }

  const wrapper = (
    complaint: JSX.Element,
    onMouseEnter: React.MouseEventHandler<HTMLDivElement> | undefined,
    onMouseLeave: React.MouseEventHandler<HTMLDivElement> | undefined
  ) => (
    <div className={css.popupHeader} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} aria-hidden>
      {complaint}
      <span onClick={() => popupRef.current?.close()} /* NOSONAR */>×</span>
    </div>
  );

  return (
    <Popup className={css.markerPopup} closeButton={false} keepInView ref={popupRef} closeOnClick>
      <Complaint
        entityId={data.id}
        entityType={ComplaintEntityType.MAP}
        key={data.coords.x + data.coords.y}
        targetTitle={data.title + `(${data.coords.x}, ${data.coords.y})`}
        triggerVisible
        className={complaintCss.businessMapComplaint}
        wrapper={wrapper}
      />
      <div className={css.popupContentContainer}>
        <h1>{data.title}</h1>
        <p>{category.name}</p>
        <a
          href={`https://www.google.com/maps/dir/Current+Location/${data.coords.x},${data.coords.y}/@${data.coords.x},${data.coords.y},17z`}
          target='_blank'
          rel='noopener, noreferrer'>
          <RouteIcon />
          <p>Прокласти маршрут</p>
        </a>
        <div className={css.group}>
          <DiscountIcon />
          <p>Знижка: {data.discount}</p>
        </div>
        <div className={css.group}>
          <RequirementsIcon />
          <span>
            <p>Необхідні документи:&nbsp;</p>
            <p>{data.requirements}</p>
          </span>
        </div>
        {data.url && (
          <div className={classNames(css.group, css.clickable)}>
            <UrlIcon />
            <a href={data.url} target='_blank' rel='noopener, noreferrer'>
              {data.url}
            </a>
            <CopyIcon className={css.copyIcon} onClick={() => copyToClipboard(data.url)} />
          </div>
        )}
        {data.phone && (
          <div className={classNames(css.group, css.clickable)}>
            <PhoneIcon />
            <a href={'tel:' + data.phone.replace(/\s/g, '')} className={css.phone}>
              {data.phone}
            </a>
            <CopyIcon className={css.copyIcon} onClick={() => copyToClipboard(data.phone)} />
          </div>
        )}
      </div>
    </Popup>
  );
}
